import React, {
 FunctionComponent, useRef, useState, useEffect
} from 'react';
import { MenuItem, Button, Alignment } from '@blueprintjs/core';
import { MultiSelect } from '@blueprintjs/select';
import { FormTemplateOption } from 'data/template/template.types';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { defaultultiselectTypeaheadPredicate, defaultMultiselectTypeaheadRenderer, MultiselectTypeahead } from '../multiselectTypeahead';
import advancedSelectCreateItemRenderer from '../AdvancedSelect/advancedSelectCreateItemRenderer';

const AdvancedMultiSelectType = MultiSelect.ofType<MultiselectTypeahead>();

type Props = {
  id?: string;
  tabIndex?: number;
  className?: string;
  disabled?: boolean;
  options: MultiselectTypeahead[];
  allowCreate?: boolean;
  placeholder: string;
  name: string;
  query: string;
  fieldValue?: MultiselectTypeahead[];
  'data-testid'?: string;
  onChange?: (name: any, item: any) => void;
  onBlur?: (name: string) => void;
  onCreateNew?: (name: string, item?: MultiselectTypeahead) => void;
  onRemoveTag?: (name: any, item: any, index: number) => void;
};

export const AdvancedMultiSelect: FunctionComponent<Props> = ({
  id,
  tabIndex,
  className,
  disabled,
  placeholder,
  options,
  name,
  query,
  fieldValue = [],
  allowCreate,
  'data-testid': dataTestId,
  onChange = () => null,
  onBlur = () => null,
  onCreateNew = () => null,
  onRemoveTag = () => null
}: Props) => {
  const isCreatingNew = useRef(false);
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<MultiselectTypeahead[]>([]);

  const createItem = (query: string): FormTemplateOption & { isNew?: true } => {
    return {
      label: query,
      value: query,
      isNew: true,
    };
  };

  useEffect(() => {
    setSelectedItems(fieldValue);
  }, [fieldValue]);

  let maybeCreateNewItemFromQuery: typeof createItem | undefined;
  let maybeCreateNewItemRenderer: typeof advancedSelectCreateItemRenderer | undefined;

  if (allowCreate) {
    maybeCreateNewItemFromQuery = createItem;
    maybeCreateNewItemRenderer = advancedSelectCreateItemRenderer;
  }

  const handleItemSelect = (item: FormTemplateOption & { isNew?: true }, event?: React.SyntheticEvent<HTMLElement, Event> | undefined) => {
    const found = selectedItems.find((s) => s.label === item.label && s.value === item.value);
    let items = [...cloneDeep(selectedItems)] as MultiselectTypeahead[];
    if (!found) {
      items = [...items, item];
      if (item.isNew === true) {
        isCreatingNew.current = true;
        onCreateNew(name, item);
      } else {
        setSelectedItems(items);
        onChange(name, items);
      }
    }
  };

  const handlePopupClose = (event?: React.SyntheticEvent<HTMLElement, Event>) => {
    if (isCreatingNew.current === true) {
      isCreatingNew.current = false;
      return;
    }

    if (event == null) {
      // The event was not triggered by the user, so should be ignored in this point.
      return;
    }

    onBlur(name);
  };

  const renderTag = (option: FormTemplateOption) => {
    return option.label;
  };

  const removeTagItem = (val: React.ReactNode, index: number) => {
    onRemoveTag(name, selectedItems[index], index);
  }

  return (
    <AdvancedMultiSelectType
      data-testid={dataTestId}
      itemPredicate={defaultultiselectTypeaheadPredicate}
      itemRenderer={defaultMultiselectTypeaheadRenderer}
      className={`advanced-select ${className}`}
      tagRenderer={renderTag}
      items={options}
      noResults={<MenuItem disabled text={t('common.form.noResults')} />}
      onItemSelect={handleItemSelect}
      tagInputProps={{ disabled, onRemove: removeTagItem }}
      // createNewItemPosition="first"
      // matchTargetWidth
      createNewItemFromQuery={maybeCreateNewItemFromQuery}
      createNewItemRenderer={maybeCreateNewItemRenderer}
      // query={query}
      popoverProps={{
        minimal: true,
        fill: true,
        usePortal: false,
        wrapperTagName: 'div',
        onClose: handlePopupClose,
      }}
      resetOnSelect
      selectedItems={selectedItems}
      // disabled={disabled}
    />
  );
};

export default AdvancedMultiSelect;
